import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Amanda Silva',
    role: 'Micropigmentadora',
    location: 'São Paulo, SP',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face',
    content: 'A Ritha transformou minha carreira! Antes eu tinha medo de cobrar o que meu trabalho valia. Depois do curso, tripliquei meu faturamento e tenho agenda cheia. O método prático faz toda diferença.',
    rating: 5,
    highlight: 'Tripliquei meu faturamento',
  },
  {
    id: 2,
    name: 'Carla Mendes',
    role: 'Esteticista',
    location: 'Rio de Janeiro, RJ',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
    content: 'O curso de Jato de Plasma foi um divisor de águas na minha clínica. A Ritha ensina com uma didática incrível e o suporte pós-curso é sensacional. Recomendo de olhos fechados!',
    rating: 5,
    highlight: 'Divisor de águas na minha clínica',
  },
  {
    id: 3,
    name: 'Patrícia Oliveira',
    role: 'Empreendedora',
    location: 'Belo Horizonte, MG',
    image: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=150&h=150&fit=crop&crop=face',
    content: 'Saí do zero e hoje tenho meu próprio studio graças aos ensinamentos da Ritha. Ela não ensina só técnica, ensina a pensar como empresária. Melhor investimento da minha vida!',
    rating: 5,
    highlight: 'Hoje tenho meu próprio studio',
  },
  {
    id: 4,
    name: 'Juliana Costa',
    role: 'Massoterapeuta',
    location: 'Curitiba, PR',
    image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face',
    content: 'A técnica do "Bumbum na Nuca" é simplesmente revolucionária! Minhas clientes ficam impressionadas com os resultados e eu me destaco no mercado. A Ritha é uma mestre.',
    rating: 5,
    highlight: 'Me destaco no mercado',
  },
];

const TestimonialCard = ({ testimonial, isActive }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: isActive ? 1 : 0.5, scale: isActive ? 1 : 0.95 }}
      transition={{ duration: 0.4 }}
      className={`glass-card rounded-3xl p-8 lg:p-10 ${
        isActive ? 'border-gold/30' : 'border-white/5'
      }`}
      data-testid={`testimonial-${testimonial.id}`}
    >
      {/* Quote icon */}
      <div className="mb-6">
        <Quote className="w-10 h-10 text-gold/30" />
      </div>

      {/* Content */}
      <p className="text-white/80 text-lg lg:text-xl leading-relaxed mb-6 font-light">
        "{testimonial.content}"
      </p>

      {/* Highlight */}
      <div className="inline-block bg-gold/10 border border-gold/30 rounded-full px-4 py-2 mb-8">
        <p className="text-gold text-sm font-semibold">{testimonial.highlight}</p>
      </div>

      {/* Author */}
      <div className="flex items-center gap-4">
        <img
          src={testimonial.image}
          alt={testimonial.name}
          className="w-14 h-14 rounded-full object-cover border-2 border-gold/30"
        />
        <div>
          <p className="text-white font-semibold">{testimonial.name}</p>
          <p className="text-white/50 text-sm">{testimonial.role}</p>
          <p className="text-gold/70 text-xs">{testimonial.location}</p>
        </div>
        
        {/* Rating */}
        <div className="ml-auto flex gap-1">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star key={i} className="w-4 h-4 text-gold fill-gold" />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="depoimentos"
      ref={ref}
      data-testid="testimonials-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-dark-paper" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      
      {/* Decorative */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-gold font-medium tracking-wider uppercase text-sm mb-4">
            Depoimentos
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Histórias de <span className="text-gold">Sucesso</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Veja o que nossas alunas têm a dizer sobre a experiência na Ritha Barreto Academy.
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          <TestimonialCard
            testimonial={testimonials[currentIndex]}
            isActive={true}
          />

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <motion.button
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </motion.button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? 'w-8 bg-gold'
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>

            <motion.button
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
